// Yazı kademesi yalnızca ölçü taşır; renk temasına dokunmaz.
// Kaynak: toplantı tablosundaki mevcut small/medium/large değerleri (W1).

export const FONT_SCALE_STORAGE_KEY = 'kurulsis-font-scale';
export const FONT_SCALE_IDS = ['small', 'medium', 'large'];

const createFontScalePatch = ({ body, title, heading, avatar, icon, chip, chipHeight, dialogTitle }) => {
    const bodyPx = `${body}px`;
    const titlePx = `${title}px`;
    const headingPx = `${heading}px`;
    const chipPx = `${chip}px`;
    const iconPx = `${icon}px`;
    const avatarFont = Math.round(Number(avatar) * 0.6);

    return {
        typography: {
            fontSize: body,
            htmlFontSize: 16,
            h1: { fontSize: headingPx },
            h2: { fontSize: headingPx },
            h3: { fontSize: titlePx },
            h4: { fontSize: titlePx },
            h5: { fontSize: titlePx },
            h6: { fontSize: titlePx },
            subtitle1: { fontSize: titlePx },
            subtitle2: { fontSize: bodyPx },
            body1: { fontSize: bodyPx },
            body2: { fontSize: bodyPx },
            button: { fontSize: bodyPx },
            caption: { fontSize: bodyPx },
            overline: { fontSize: bodyPx },
        },
        components: {
            MuiTableCell: {
                styleOverrides: {
                    root: { fontSize: bodyPx },
                    head: { fontSize: bodyPx },
                },
            },
            MuiAvatar: {
                styleOverrides: {
                    root: { width: avatar, height: avatar, fontSize: avatarFont },
                },
            },
            MuiAvatarGroup: {
                styleOverrides: {
                    avatar: { width: avatar, height: avatar, fontSize: avatarFont },
                },
            },
            MuiChip: {
                styleOverrides: {
                    // Jumbo default.js: sizeSmall 12px / 22px; kademede aynı oran.
                    sizeSmall: { height: chipHeight, fontSize: chipPx },
                    labelSmall: { fontSize: chipPx },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: { fontSize: bodyPx },
                    sizeSmall: { fontSize: bodyPx },
                },
            },
            MuiSvgIcon: {
                styleOverrides: {
                    root: { fontSize: iconPx },
                    fontSizeSmall: { fontSize: `${Math.max(12, icon - 3)}px` },
                    fontSizeLarge: { fontSize: `${icon + 4}px` },
                },
            },
            MuiDialogTitle: {
                styleOverrides: {
                    root: { fontSize: `${dialogTitle}px` },
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    root: { fontSize: bodyPx },
                    input: { fontSize: bodyPx },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: { fontSize: bodyPx },
                },
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: { fontSize: bodyPx },
                },
            },
            MuiListItemText: {
                styleOverrides: {
                    primary: { fontSize: bodyPx },
                    secondary: { fontSize: bodyPx },
                },
            },
            MuiTablePagination: {
                styleOverrides: {
                    root: { fontSize: bodyPx },
                    selectLabel: { fontSize: bodyPx },
                    displayedRows: { fontSize: bodyPx },
                    select: { fontSize: bodyPx },
                },
            },
            MuiCardHeader: {
                styleOverrides: {
                    title: { fontSize: titlePx },
                },
            },
            MuiFormControlLabel: {
                styleOverrides: {
                    label: { fontSize: bodyPx },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: { fontSize: bodyPx },
                },
            },
            MuiFab: {
                styleOverrides: {
                    root: { width: avatar, height: avatar, minHeight: avatar },
                },
            },
        },
    };
};

export const FONT_SCALES = {
    small: createFontScalePatch({
        body: 10,
        title: 12,
        heading: 13,
        avatar: 25,
        icon: 15,
        chip: 11,
        chipHeight: 20,
        dialogTitle: 13,
    }),
    medium: createFontScalePatch({
        body: 11,
        title: 14,
        heading: 15,
        avatar: 28,
        icon: 18,
        chip: 12,
        chipHeight: 22,
        dialogTitle: 18,
    }),
    large: createFontScalePatch({
        body: 13,
        title: 16,
        heading: 17,
        avatar: 36,
        icon: 21,
        chip: 14,
        chipHeight: 24,
        dialogTitle: 21,
    }),
};

const mergeStyleOverrides = (base = {}, patch = {}) => {
    const keys = new Set([...Object.keys(base), ...Object.keys(patch)]);
    const out = {};
    keys.forEach((key) => {
        const baseVal = base[key];
        const patchVal = patch[key];
        if (
            baseVal &&
            patchVal &&
            typeof baseVal === 'object' &&
            typeof patchVal === 'object' &&
            !Array.isArray(baseVal) &&
            !Array.isArray(patchVal)
        ) {
            out[key] = { ...baseVal, ...patchVal };
        } else {
            out[key] = patchVal !== undefined ? patchVal : baseVal;
        }
    });
    return out;
};

export const mergeFontScale = (theme, scaleId) => {
    const patch = FONT_SCALES[scaleId] || FONT_SCALES.medium;
    const baseComponents = theme?.components || {};
    const patchComponents = patch.components || {};
    const componentKeys = new Set([
        ...Object.keys(baseComponents),
        ...Object.keys(patchComponents),
    ]);
    const components = {};
    componentKeys.forEach((key) => {
        const base = baseComponents[key] || {};
        const extra = patchComponents[key] || {};
        components[key] = {
            ...base,
            ...extra,
            styleOverrides: mergeStyleOverrides(base.styleOverrides, extra.styleOverrides),
            defaultProps: { ...base.defaultProps, ...extra.defaultProps },
        };
    });

    return {
        typography: {
            ...theme?.typography,
            ...patch.typography,
            h1: { ...theme?.typography?.h1, ...patch.typography.h1 },
            h2: { ...theme?.typography?.h2, ...patch.typography.h2 },
            h3: { ...theme?.typography?.h3, ...patch.typography.h3 },
            h4: { ...theme?.typography?.h4, ...patch.typography.h4 },
            h5: { ...theme?.typography?.h5, ...patch.typography.h5 },
            h6: { ...theme?.typography?.h6, ...patch.typography.h6 },
        },
        components,
    };
};

export const getStoredFontScale = () => {
    try {
        const value = window.localStorage.getItem(FONT_SCALE_STORAGE_KEY);
        if (FONT_SCALE_IDS.includes(value)) return value;
    } catch (e) {
        // private mode
    }
    return 'medium';
};

export const storeFontScale = (scaleId) => {
    try {
        window.localStorage.setItem(FONT_SCALE_STORAGE_KEY, scaleId);
    } catch (e) {
        // private mode
    }
};

export const applyFontScaleToLayers = ({
    theme,
    setTheme,
    headerTheme,
    setHeaderTheme,
    sidebarTheme,
    setSidebarTheme,
    footerTheme,
    setFooterTheme,
}, scaleId) => {
    const mainPatch = mergeFontScale(theme, scaleId);
    setTheme(mainPatch);
    if (setHeaderTheme && headerTheme) {
        const headerPatch = mergeFontScale(headerTheme, scaleId);
        setHeaderTheme({ ...headerTheme, ...headerPatch });
    }
    if (setSidebarTheme && sidebarTheme) {
        const sidebarPatch = mergeFontScale(sidebarTheme, scaleId);
        setSidebarTheme({ ...sidebarTheme, ...sidebarPatch });
    }
    if (setFooterTheme && footerTheme) {
        const footerPatch = mergeFontScale(footerTheme, scaleId);
        setFooterTheme({ ...footerTheme, ...footerPatch });
    }
};
